import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../contexts/AuthContext';
import { kv } from '../runtime/mobile/kv';
import { INTRO_SLIDES } from '../content/guide';
import { colors, radius, spacing, typography } from '../config/theme';
import { Sticker } from './brand';

const SLIDE_STICKERS = ['matreshka', 'samovar', 'kiosk', 'bufet'];

// Shown once per account, right after the first login: a short set of cards you
// tap through. No horizontal scrolling / Dimensions math — it broke on iOS
// standalone. The full, always-available version lives in the «Памятка» tab.
export default function FirstRunGuide() {
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [visible, setVisible] = useState(false);
  const [i, setI] = useState(0);
  const storeKey = user ? `guide:seen:${user.uid}` : null;

  useEffect(() => {
    if (!storeKey) return undefined;
    let alive = true;
    kv.get(storeKey).then((seen) => { if (alive && !seen) { setI(0); setVisible(true); } }).catch(() => {});
    return () => { alive = false; };
  }, [storeKey]);

  const close = () => {
    if (storeKey) kv.set(storeKey, '1').catch(() => {});
    setVisible(false);
  };

  const slide = INTRO_SLIDES[i];
  const last = i >= INTRO_SLIDES.length - 1;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={close} statusBarTranslucent>
      <View style={[styles.backdrop, { paddingTop: insets.top + spacing.lg, paddingBottom: Math.max(insets.bottom, spacing.lg) }]}>
        <View style={styles.sheet}>
          <View style={styles.stepline}>
            <Text style={styles.stepText}>{i + 1} / {INTRO_SLIDES.length}</Text>
            {!last ? (
              <Pressable onPress={close} hitSlop={12}><Text style={styles.skip}>Пропустить</Text></Pressable>
            ) : <View />}
          </View>

          <View style={styles.body}>
            <Sticker name={SLIDE_STICKERS[i] || 'matreshka'} size={120} style={styles.slideSticker} />
            <Text style={styles.title}>{slide.title}</Text>
            <Text style={styles.text}>{slide.body}</Text>
          </View>

          <View style={styles.dots}>
            {INTRO_SLIDES.map((s, idx) => (
              <View key={s.title} style={[styles.dot, idx === i && styles.dotOn]} />
            ))}
          </View>

          <Pressable style={styles.cta} onPress={() => (last ? close() : setI(i + 1))}>
            <Text style={styles.ctaText}>{last ? 'Начать работу' : 'Дальше'}</Text>
          </Pressable>
          {i > 0 && !last ? (
            <Pressable onPress={() => setI(i - 1)} hitSlop={8} style={styles.backBtn}>
              <Text style={styles.backText}>Назад</Text>
            </Pressable>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(34,31,31,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  sheet: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: colors.surfaceCard,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    padding: spacing.lg,
  },
  stepline: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md },
  stepText: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 1 },
  skip: { ...typography.caption, color: colors.textMuted, fontWeight: '700' },
  body: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.lg },
  slideSticker: { marginBottom: spacing.xs },
  title: { ...typography.h2, color: colors.textPrimary, textAlign: 'center' },
  text: { ...typography.body, fontSize: 15.5, lineHeight: 23, color: colors.textSecondary, textAlign: 'center' },
  dots: { flexDirection: 'row', gap: 6, justifyContent: 'center', marginVertical: spacing.md },
  dot: { width: 7, height: 7, borderRadius: 4, backgroundColor: colors.border },
  dotOn: { backgroundColor: colors.primary, width: 20 },
  cta: {
    backgroundColor: colors.primary,
    borderRadius: radius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  ctaText: { ...typography.button, color: colors.onPrimary, fontSize: 15 },
  backBtn: { alignItems: 'center', paddingTop: spacing.sm },
  backText: { ...typography.caption, color: colors.textMuted, fontWeight: '700' },
});
